import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { FormControl, RadioGroup } from '@material-ui/core';
import { TextField, Button } from '@mui/material';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { CampoObrigatorio } from '../../../../components';
import Grid24 from '../../../../components/UI/Grid24';
import SubTitleForm from '../../../../components/UI/SubTitleForm/SubTitleForm';
import RadioField from '../../../../components/UI/Field/Radio/RadioField';
import {
  getFieldName,
  getMoneyMask,
  haveExceptionValidations,
  isBadRequestException,
  makeErrorField,
  toBrazilianDate,
  useUtilStyles,
} from '../../../../helpers';
import useStyle from '../AcordosStyle';
import { analiseSimulacaoController } from './dialogAnaliseSimulacaoController';
import { findTitulosAcordosProcessoService } from '../../../../services/core/processos/processosTitulosService';
import TooltipUI from '../../../../components/UI/Tooltip/Tooltip';
import { loadingInspecaoProcessoController } from '../../loadingInspecaoProcessoController';
import { findQuantidadeAutorizacoesAction } from '../../../../store/theme.actions';
import TabCardLayout from '../../../../layouts/TabCardLayout/v2/TabCardLayout';
import TabPanel from '../../../../components/UI/TabPanel/TabPanel';
import { AcompanhamentoProcesso } from '../../Acompanhamento/Acompanhamento';
import TabelaPropostaAcordos from './TabelaPropostaAcordos';
import { buildSettingsTable } from '../../../../helpers/factory/global.factory';
import {
  buildTable,
  formaPgtoString,
  tabItems,
  tableColumns,
} from './TabsInfoBuilder';


const RADIO_LABELS = {
  aprovada: 'Aprovada',
  naoAprovada: 'Não Aprovada',
  contraProposta: 'Contraproposta',
};

function DialogAnaliseSimulacao({
  setOpenDialogAnaliseSimulacao,
  selectedAcordoAnaliseSimulacao,
  updatePage,
  setLoadings,
  pesquisarAcordos,
  getDadosResumo,
  settingsTable,
  columnOrdenacao,
  idProcesso,
  setInfoAdicionaisAcordo,
  exibirApenasAcordosAtivosEhPropostas,
  textoHistorico,
  setOpenButtonNovoAcordo,
  setIsContraProposta,
  todosAcordos,
  dadosProcesso,
}) {
  const [formFields, setFormFields] = useState(analiseSimulacaoController.makeFormFields());
  const [errorFields, setErrorFields] = useState(analiseSimulacaoController.makeErrorFields());

  const utilStyles = useUtilStyles();
  const style = useStyle();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState(0);
  const processo = useMemo(() => ({ id: idProcesso }), [idProcesso]);

  // Estados para controle da tabela de acordos
  const [acordosSettingsTable, setAcordosSettingsTable] = useState(buildSettingsTable([], 0, 10, 0));
  const [tableRows, setTableRows] = useState([]);
  const [ultimaConsultaTabela, setUltimaConsultaTabela] = useState(null);

  // Buscar acordo completo com todas as parcelas da lista de todos os acordos
  const acordoCompleto = useMemo(() => {
    if (!todosAcordos || todosAcordos.length === 0) {
      return selectedAcordoAnaliseSimulacao;
    }
    const acordo = todosAcordos.find(
      a => a.idAcordo === selectedAcordoAnaliseSimulacao?.idAcordo,
    );
    return acordo || selectedAcordoAnaliseSimulacao;
  }, [todosAcordos, selectedAcordoAnaliseSimulacao]);

  const parcelasAcordo = useMemo(
    () => acordoCompleto?.parcelas || [],
    [acordoCompleto],
  );

  const changeTabViewHandler = useCallback(
    (activeTabParam) => {
      setActiveTab(activeTabParam);
    },
    [setActiveTab],
  );

  const onClose = useCallback(() => {
    setOpenDialogAnaliseSimulacao(false);
  }, [setOpenDialogAnaliseSimulacao]);

  async function onClickSubmitAnalise(e) {
    e.preventDefault();
    setLoadings(loadingInspecaoProcessoController.changeLoadingAcordos(true));
    try {
      const isContraproposta = formFields.simulacaoAprovada.value === 'CP';
      const isAprovada = formFields.simulacaoAprovada.value === 'A';

      // Se for Contraproposta, envia como "NA" (Não Aprovada)
      let result;
      if (isAprovada) {
        result = await analiseSimulacaoController.aprovadaSimulacao(formFields, selectedAcordoAnaliseSimulacao.idAcordo);
      } else {
        result = await analiseSimulacaoController.desaprovarSimulacao(formFields, selectedAcordoAnaliseSimulacao.idAcordo);
      }

      const { codResult, msgResult } = result.data;
      if (codResult > 0) {
        if (codResult === 1) {
          toast.warn(msgResult);
        } else {
          toast.error(msgResult, { style: { width: '392px' } });
        }
      }
      if (codResult === 0) {
        toast.success('Acordo gerado com sucesso.');
      }

      pesquisarAcordos(settingsTable.page, settingsTable.rowsPerPage, columnOrdenacao, exibirApenasAcordosAtivosEhPropostas);
      getDadosResumo(idProcesso).then((responseData) => {
        const { data } = responseData;
        setInfoAdicionaisAcordo(data);
      });
      updatePage();
      dispatch(findQuantidadeAutorizacoesAction());
      onClose();

      // Se for Contraproposta, abre NovoAcordo
      if (isContraproposta) {
        setOpenButtonNovoAcordo(true);
        setIsContraProposta(true);
      }
    } catch (error) {
      if (isBadRequestException(error)) {
        if (haveExceptionValidations(error)) {
          const validations = error?.response?.data?.validations;
          validations.forEach((validation) => {
            setErrorFields((errFields) => {
              errFields[validation.field].error = true;
              errFields[validation.field].errorMessage = validation.message;
              return errFields;
            });
          });
        } else {
          toast.error(error.response.data.message, { style: { width: '392px' } });
          pesquisarAcordos(settingsTable.page, settingsTable.rowsPerPage, columnOrdenacao, exibirApenasAcordosAtivosEhPropostas);
          getDadosResumo(idProcesso).then((responseData) => {
            const { data } = responseData;
            setInfoAdicionaisAcordo(data);
          });
          updatePage();
          onClose();
        }
      }
    } finally {
      setLoadings(loadingInspecaoProcessoController.changeLoadingAcordos(false));
    }
  }

  const onChangeHandler = useCallback((e) => {
    const name = getFieldName(e.target);
    const { value } = e.target;
    setFormFields(oldValue => ({ ...oldValue, [name]: { ...oldValue[name], value } }));
  }, []);

  const onFocusHandler = useCallback((e) => {
    const name = getFieldName(e.target);
    setErrorFields(oldValues => ({ ...oldValues, [name]: makeErrorField() }));
  }, []);

  // Buscar títulos do processo
  useEffect(() => {
    const idAcordo = selectedAcordoAnaliseSimulacao?.idAcordo;
    if (!idProcesso || !idAcordo) return;

    const chaveConsulta = `${idProcesso}-${idAcordo}`;
    if (ultimaConsultaTabela === chaveConsulta) return;

    setUltimaConsultaTabela(chaveConsulta);

    findTitulosAcordosProcessoService(idProcesso, idAcordo)
      .then((response) => {
        const data = response?.data || [];
        setAcordosSettingsTable({
          content: Array.isArray(data) ? data : [],
          page: 0,
          rowsPerPage: 10,
          size: Array.isArray(data) ? data.length : 0,
        });
      })
      .catch(() => {
        setUltimaConsultaTabela(null);
        setAcordosSettingsTable(buildSettingsTable([], 0, 10, 0));
      });
  }, [idProcesso, selectedAcordoAnaliseSimulacao, ultimaConsultaTabela]);

  // Construir linhas da tabela
  useEffect(() => {
    const rows = acordosSettingsTable.content.map(acordo => buildTable(acordo));
    setTableRows(rows);
  }, [acordosSettingsTable.content]);

  return (
    <>
      {errorFields?.simulacaoAprovada.error === true || errorFields.textoJustificativa.error === true ? (
        <CampoObrigatorio />
      ) : (
        ''
      )}
      <div>
        <Grid24 xs={24}>
          <FormControl style={{ width: '100%', paddingBottom: '10px' }} error={errorFields.simulacaoAprovada.error}>
            <TooltipUI open={errorFields.simulacaoAprovada.error} error title={errorFields.simulacaoAprovada.errorMessage}>
              <RadioGroup
                value={formFields.simulacaoAprovada.value}
                onChange={onChangeHandler}
                row
                className={utilStyles.padding10}
                name="simulacaoAprovada"
                onFocus={onFocusHandler}
              >
                <RadioField className={utilStyles.mr2} label={RADIO_LABELS.aprovada} value="A" />
                <RadioField className={utilStyles.mr2} label={RADIO_LABELS.naoAprovada} value="NA" />
                <RadioField className={utilStyles.mr2} label={RADIO_LABELS.contraProposta} value="CP" />
              </RadioGroup>
            </TooltipUI>
          </FormControl>
        </Grid24>
        <Grid24 style={{ display: 'flex', flexDirection: 'column' }}>
          <TabCardLayout
            tabItems={tabItems}
            parentStates={[activeTab, changeTabViewHandler]}
          >
            <TabPanel
              id={tabItems[0].id}
              index={tabItems[0].index}
              value={activeTab}
            >
              <TextField
                style={{ width: '100%' }}
                name="ultimoHistorico"
                InputProps={{
                  style: { fontSize: '13px' },
                }}
                value={textoHistorico}
                className={style.textFieldUltimoHistorico}
                defaultValue=""
                rows={9}
                multiline
                disabled
              />
            </TabPanel>
            <TabPanel
              id={tabItems[1].id}
              index={tabItems[1].index}
              value={activeTab}
            >
              <AcompanhamentoProcesso
                processo={processo}
                setLoadings={setLoadings}
                dadosProcesso={dadosProcesso}
              />
            </TabPanel>
            <TabPanel
              id={tabItems[2].id}
              index={tabItems[2].index}
              value={activeTab}
            >
              <TabelaPropostaAcordos
                rows={tableRows}
                columns={tableColumns}
                rowsPerPage={acordosSettingsTable.rowsPerPage}
              />
            </TabPanel>
            <TabPanel
              id={tabItems[3].id}
              index={tabItems[3].index}
              value={activeTab}
            >
              <TextField
                style={{ width: '100%' }}
                name="parcelas"
                InputProps={{
                  style: { fontSize: '13px' },
                }}
                value={parcelasAcordo.map((parcela, index) => (
                  `Parcela ${parcela.nroParcela ?? index + 1}: ${formaPgtoString(parcela.idTipoFormaPagto)}`
                      + ` - Vencimento: ${toBrazilianDate(parcela.dataVencimento)}`
                      + ` - Valor: ${getMoneyMask(parcela.valor)}`
                )).join('\n')}
                className={style.textFieldUltimoHistorico}
                defaultValue=""
                rows={9}
                multiline
                disabled
              />
            </TabPanel>
          </TabCardLayout>
        </Grid24>
        <Grid24 style={{ display: 'flex', flexDirection: 'column' }}>
          <SubTitleForm>
            Justificativa
          </SubTitleForm>
          <TooltipUI style={{ marginTop: '12px', marginBottom: '22px' }} open={errorFields.textoJustificativa.error} error title={errorFields.textoJustificativa.errorMessage}>
            <TextField
              name="textoJustificativa"
              label="Texto"
              InputProps={{
                style: { fontSize: '13px' },
              }}
              id="outlined-error"
              value={formFields.textoJustificativa.value}
              className={style.textFieldScrollBarBox}
              defaultValue=""
              onChange={onChangeHandler}
              rows={11}
              multiline
              onFocus={onFocusHandler}
              error={errorFields.textoJustificativa.error}
            />
          </TooltipUI>
        </Grid24>
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            disabled={formFields.simulacaoAprovada.value === ''
              || formFields.simulacaoAprovada.value === null
              || formFields.simulacaoAprovada.value === undefined}
            onClick={onClickSubmitAnalise}
          >
            OK
          </Button>
        </div>
      </div>

    </>
  );
}


export default DialogAnaliseSimulacao;
